import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Compass, ShieldAlert, CalendarDays, Gift, Heart, User, LogIn,
  BadgeCheck, HeartHandshake, ChevronRight, Briefcase,
} from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';
import NotificationBell from '../components/NotificationBell';
import BrowserCompatibilityNotice from '../components/BrowserCompatibilityNotice';
import InstallAppMenuItem from '../components/InstallAppMenuItem';
import CommunityPartnerCard from '../components/CommunityPartnerCard';
import EmergencyBanner from '../components/EmergencyBanner';
import { AuthContext, API } from '../App';
import { useFlags } from '../hooks/useFlags';
import { useDashT } from '../components/dashStrings';

/**
 * FrontPage — "Soft Bento" home dashboard (Jul 2026 redesign, user-approved).
 *
 * Light slate canvas, sticky blurred header, 6 primary tiles in a bento grid
 * (Discover + Community Safety Hub as full-width heroes), staggered entrance
 * animation, live incident pill on the Safety Hub tile, and a muted
 * "For Businesses" card below. All routes unchanged.
 */

const TILE_STYLES = {
  discover: { icon: Compass, iconBg: 'bg-emerald-500', ring: 'hover:border-emerald-200' },
  safety: { icon: ShieldAlert, iconBg: 'bg-red-500', ring: 'hover:border-red-200' },
  events: { icon: CalendarDays, iconBg: 'bg-violet-500', ring: 'hover:border-violet-200' },
  offers: { icon: Gift, iconBg: 'bg-amber-500', ring: 'hover:border-amber-200' },
  favourites: { icon: Heart, iconBg: 'bg-rose-500', ring: 'hover:border-rose-200' },
  profile: { icon: User, iconBg: 'bg-indigo-500', ring: 'hover:border-indigo-200' },
};

const BentoTile = ({ tileKey, title, subtitle, route, hero = false, delay = 0, badge = null, navigate }) => {
  const { icon: Icon, iconBg, ring } = TILE_STYLES[tileKey];
  return (
    <button
      onClick={() => navigate(route)}
      style={{ animationDelay: `${delay}ms` }}
      className={`dash-tile-enter relative text-left bg-white border border-slate-100 ${ring} rounded-[1.75rem] shadow-sm hover:shadow-md hover:-translate-y-1 active:scale-[0.98] transition-[transform,box-shadow,border-color] duration-300 ${
        hero ? 'col-span-2 p-5 sm:p-6 flex items-center gap-4 sm:gap-5' : 'col-span-1 p-4 sm:p-5 flex flex-col gap-3'
      }`}
      data-testid={`tile-${tileKey}`}
    >
      <span className={`flex shrink-0 items-center justify-center rounded-2xl text-white shadow-sm ${iconBg} ${hero ? 'h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem]' : 'h-12 w-12'}`}>
        <Icon className={hero ? 'h-8 w-8 sm:h-9 sm:w-9' : 'h-6 w-6'} aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className={`block font-extrabold text-slate-900 tracking-tight leading-tight ${hero ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg'}`}>
          {title}
        </span>
        <span className={`block text-slate-500 leading-snug mt-0.5 ${hero ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
          {subtitle}
        </span>
      </span>
      {badge}
      {hero && <ChevronRight className="h-6 w-6 text-slate-300 ml-auto shrink-0 rtl-flip" aria-hidden="true" />}
    </button>
  );
};

const FrontPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dashT = useDashT();
  const { user } = useContext(AuthContext);
  const flags = useFlags();
  const [activeIncidents, setActiveIncidents] = useState(0);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/incidents`)
      .then(r => { if (alive) setActiveIncidents(r.data.activeCount || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Logged-in users skip the landing page — preserves prior behaviour.
  if (user) {
    return <Navigate to="/discover" replace />;
  }

  const businessLinks = [
    { key: 'signup', icon: Briefcase, label: dashT('businessSignUp'), route: '/business/signup', testId: 'business-link-signup' },
    { key: 'claim', icon: BadgeCheck, label: t('frontpage.claimExistingBusinessTitle', 'Claim or Add a Business'), route: '/claim-your-business', testId: 'business-link-claim' },
    { key: 'charity', icon: HeartHandshake, label: t('frontpage.charityApplyTitle', 'Community / Charity Listing'), route: '/charity/apply', testId: 'business-link-charity' },
    ...(flags.affiliatesEnabled
      ? [{ key: 'affiliate', icon: Gift, label: t('frontpageEarnCommission', 'Become a Community Partner'), route: '/affiliate', testId: 'business-link-affiliate' }]
      : []),
    { key: 'signin', icon: LogIn, label: t('home.businessSignInTitle', 'Already have a Business Account? Sign In'), route: '/signin', testId: 'business-link-signin' },
    { key: 'calendar', icon: CalendarDays, label: t('home.businessCalendarTitle', 'Business Calendar'), route: '/calendar', testId: 'business-link-calendar',
      onClick: () => { if (user) { navigate('/calendar'); } else { navigate('/signin', { state: { from: '/calendar' } }); } } },
  ];

  const incidentBadge = activeIncidents > 0 ? (
    <span
      className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-red-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow"
      data-testid="tile-safety-incident-pill"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
      </span>
      {activeIncidents} {activeIncidents === 1 ? dashT('activeIncidents') : dashT('activeIncidentsPlural')}
    </span>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky blurred header — logo, sign-in, bell, language */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center gap-3">
          <img
            src="/logo.jpg"
            alt="Find-2-Day Logo"
            className="w-9 h-9 rounded-xl shadow-sm"
            data-testid="app-logo"
          />
          <h1 className="text-lg font-extrabold text-purple-700 tracking-tight" data-testid="app-title">
            {t('appTitle')}
          </h1>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              onClick={() => navigate('/signin')}
              className="text-slate-600 hover:text-slate-900 text-sm font-semibold px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="header-sign-in-button"
            >
              {t('signIn', 'Sign in')}
            </button>
            <NotificationBell variant="on-white" />
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-6 pb-12">
        {/* Value statement — permanent, replaces greeting per user choice */}
        <p
          className="text-lg sm:text-xl font-semibold text-slate-800 leading-snug mb-5 dash-tile-enter"
          data-testid="app-tagline"
        >
          {dashT('valueStatement')}
        </p>

        <BrowserCompatibilityNotice />

        {/* Emergency banner — always the highest visual priority */}
        <EmergencyBanner />

        {/* Feb 2026 v3 — home simplified. Only two primary destinations:
            Discover (businesses) and Community Hub. Events, Offers, Favourites
            and Profile now live only in the bottom navigation to remove
            duplication. */}
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2" data-testid="home-bento-grid">
          <BentoTile tileKey="discover" hero delay={60} title={dashT('discover')} subtitle={dashT('discoverSub')} route="/explore" navigate={navigate} />
          <BentoTile tileKey="safety" hero delay={140} title={dashT('communityHub')} subtitle={dashT('communityHubSub')} route="/community-hub" badge={incidentBadge} navigate={navigate} />
        </div>

        {/* Community Partners — prominent promo for visitors worldwide */}
        {flags.affiliatesEnabled && (
          <section className="mt-6 dash-tile-enter" style={{ animationDelay: '500ms' }} data-testid="home-community-partner-section">
            <CommunityPartnerCard />
          </section>
        )}

        {/* For Businesses — quiet secondary card */}
        <section
          className="mt-6 bg-slate-100/80 border border-slate-200/70 rounded-[1.75rem] p-4 sm:p-5 dash-tile-enter"
          style={{ animationDelay: '540ms' }}
          data-testid="home-business-section"
        >
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2 px-1">
            {dashT('forBusinesses')}
          </h2>
          <div className="grid gap-1">
            {businessLinks.map(({ key, icon: Icon, label, route, testId, onClick }) => (
              <button
                key={key}
                onClick={onClick || (() => navigate(route))}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white active:bg-slate-50 transition-colors text-left"
                data-testid={testId}
              >
                <Icon className="h-5 w-5 text-slate-500 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-sm font-semibold text-slate-800 leading-tight">{label}</span>
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 rtl-flip" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>

        {/* Install-App tile — anonymous visitors can install the PWA */}
        <div className="mt-4">
          <InstallAppMenuItem surface="frontpage" variant="card" testId="frontpage-install-app-card" />
        </div>

        {/* Legal links + admin — dark-on-light restyle, routes unchanged */}
        <div className="mt-8 flex flex-wrap justify-center gap-3 text-sm">
          <a href="/terms" className="text-slate-400 hover:text-slate-600 transition-colors">{t('terms')}</a>
          <span className="text-slate-300">|</span>
          <a href="/privacy" className="text-slate-400 hover:text-slate-600 transition-colors">{t('privacy')}</a>
          <span className="text-slate-300">|</span>
          <a href="/cookies" className="text-slate-400 hover:text-slate-600 transition-colors">{t('cookies')}</a>
          <span className="text-slate-300">|</span>
          <a href="/disputes" className="text-slate-400 hover:text-slate-600 transition-colors">{t('disputePolicy', 'Disputes')}</a>
        </div>
        <div className="mt-3 text-center">
          <button
            onClick={() => navigate('/admin/login')}
            className="text-slate-300 text-xs hover:text-slate-500 transition-colors"
            data-testid="footer-admin-link"
          >
            {t('admin')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default FrontPage;
